import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Layers, Library, Gavel, Bot, ArrowRight, Route, Waypoints, Sparkles, Compass, Users, Scale } from "lucide-react";
import { motion } from "framer-motion";
import { SupporterBadge } from "@/components/SupporterBadge";
import OracleWidget from "@/components/OracleWidget";

import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({ lessons: [] });
  const [counts, setCounts] = useState({ decks: 0, terms: 0, templates: 0, lessons: 0 });

  const manageBilling = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch {
      toast.error("No active subscription to manage yet.");
    }
  };

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/flashcards").then((r) => setCounts((c) => ({ ...c, decks: r.data.length })));
    api.get("/dictionary").then((r) => setCounts((c) => ({ ...c, terms: r.data.length })));
    api.get("/templates").then((r) => setCounts((c) => ({ ...c, templates: r.data.length })));
    api.get("/meta").then((r) => setCounts((c) => ({ ...c, lessons: r.data.lesson_count || 0 })));
  }, []);

  const totalLessons = Math.max(1, counts.lessons);
  const done = (progress.lessons || []).length;
  const pct = Math.min(100, Math.round((done / totalLessons) * 100));

  const tiles = [
    { icon: Route, label: "Study Path", to: "/study-path", meta: "Guided order" },
    { icon: Waypoints, label: "Rabbit Hole", to: "/map", meta: "Visual map" },
    { icon: Compass, label: "Teachings", to: "/teachings", meta: "Teachers & sources" },
    { icon: Users, label: "Community", to: "/community", meta: "Connect & build" },
    { icon: BookOpen, label: "Courses", to: "/courses", meta: `${courses.length} available` },
    { icon: Layers, label: "Flashcards", to: "/flashcards", meta: `${counts.decks} decks` },
    { icon: Library, label: "Dictionary", to: "/dictionary", meta: `${counts.terms} terms` },
    { icon: Library, label: "Black's Law 4th", to: "/blacks", meta: "Full A–Z text" },
    { icon: Scale, label: "Statute Library", to: "/statutes", meta: "Acts · USC · UCC" },
    { icon: Gavel, label: "Case Law", to: "/caselaw", meta: "Search & ask" },
    { icon: Bot, label: "AI Assistant", to: "/assistant", meta: "Draft & ask" },
    { icon: Sparkles, label: "AI Credits", to: "/credits", meta: `${user?.ai_credits ?? 0} balance` },
  ];

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-widest font-mono text-primary">Dashboard</span>
        <div className="flex flex-wrap items-center gap-3 mt-2 mb-2">
          <h1 className="font-serif text-5xl">Welcome, {user?.name?.split(" ")[0]}.</h1>
          <SupporterBadge total={user?.total_credits_purchased || 0} supporter={user?.supporter} />
        </div>
        <div className="flex items-center gap-4 mb-12">
          <p className="text-muted-foreground">Pick up where you left off in the archive.</p>
          {user?.subscription_active && (
            <button onClick={manageBilling} data-testid="manage-subscription" className="text-xs uppercase tracking-widest font-mono text-muted-foreground hover:text-primary transition-colors border-b border-transparent hover:border-primary pb-0.5">
              Manage subscription
            </button>
          )}
          <Link to="/referrals" data-testid="dash-referrals" className="text-xs uppercase tracking-widest font-mono text-primary hover:opacity-80 transition-opacity border-b border-transparent hover:border-primary pb-0.5">
            Refer a friend, get a free month →
          </Link>
        </div>

        <div className="mb-14 py-10 border-y border-border bg-gradient-to-b from-primary/[0.06] to-transparent rounded-md" data-testid="dashboard-oracle-hero">
          <OracleWidget variant="hero" />
        </div>

        <div className="grid lg:grid-cols-3 gap-px bg-border border border-border mb-12">
          <div className="bg-card p-8 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl">Overall progress</h2>
              <span className="font-mono text-sm text-primary">{done}/{totalLessons} lessons</span>
            </div>
            <div className="h-2 bg-muted rounded-none overflow-hidden">
              <motion.div className="h-full bg-primary" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{pct}% complete</p>
          </div>
          <div className="bg-primary p-8 flex flex-col justify-center">
            <Bot className="w-8 h-8 text-primary-foreground mb-3" />
            <h3 className="font-serif text-2xl text-primary-foreground mb-2">Need a document drafted?</h3>
            <Link to="/assistant" className="text-sm text-primary-foreground/90 inline-flex items-center gap-2 hover:text-primary-foreground">
              Open AI Assistant <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-px bg-border border border-border mb-14">
          {tiles.map((t, i) => (
            <Link key={t.label} to={t.to} data-testid={`dash-tile-${t.label.toLowerCase().replace(/\s/g,"-")}`} className="bg-card p-6 group hover:bg-muted transition-colors">
              <t.icon className="w-6 h-6 text-primary mb-6" />
              <h3 className="font-serif text-xl">{t.label}</h3>
              <p className="text-xs text-muted-foreground font-mono mt-1">{t.meta}</p>
            </Link>
          ))}
        </div>

        <h2 className="font-serif text-3xl mb-6">Continue learning</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => (
            <Link key={c.slug} to={`/courses/${c.slug}`} data-testid={`dash-course-${c.slug}`} className="border border-border bg-card group hover:border-primary transition-colors">
              <div className="h-32 bg-cover bg-center grain" style={{ backgroundImage: `url('${c.image}')` }} />
              <div className="p-5">
                <span className="text-[10px] uppercase tracking-widest font-mono text-primary">{c.category}</span>
                <h3 className="font-serif text-xl mt-1 leading-tight">{c.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
